import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  signUp as amplifySignUp,
  confirmSignUp as amplifyConfirmSignUp,
  resendSignUpCode as amplifyResendSignUpCode,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  confirmSignIn as amplifyConfirmSignIn,
  getCurrentUser,
  fetchAuthSession,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { UserService } from '../services/userService';
import { UserProfile } from '../types/user';
import logger from '../utils/logger';

// #101 / #108: Amplify v6 が localStorage に置く User Pool トークンのプレフィックス。
// access/id/refresh/LastAuthUser など悪用可能なトークンはすべてこの配下に集約される。
const COGNITO_STORAGE_KEY_PREFIX = 'CognitoIdentityServiceProvider';

const isCognitoStorageKey = (key: string): boolean => key.startsWith(COGNITO_STORAGE_KEY_PREFIX);

/**
 * amplifySignOut() 失敗時のフォールバック (#101)。
 * signOut API 失敗時にトークンが localStorage に残留しないことを保証する。
 */
const wipeCognitoLocalStorageTokens = (): void => {
  const keysToRemove = Object.keys(localStorage).filter(isCognitoStorageKey);
  keysToRemove.forEach((key) => localStorage.removeItem(key));
  logger.info(`🧹 [AuthContext] Purged ${keysToRemove.length} Cognito tokens from localStorage`);
};

interface User {
  email: string;
  sub: string;  // Cognito UUID - primary identifier
  attributes?: Record<string, string>;
}

interface PendingSignUp {
  email: string;
  password: string;
  displayName: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuthState: () => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<{ destination?: string }>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  resendSignUpCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Memory-only state for sign-up flow (password is never persisted).
  // Reload during the confirm step clears this state, falling back to /login.
  const [pendingSignUp, setPendingSignUp] = useState<PendingSignUp | null>(null);

  const checkAuthState = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      logger.debug('🔍 [AuthContext] Checking auth state with Amplify v6...');

      // Get current user (throws if not authenticated)
      const currentUser = await getCurrentUser();
      const attributes = await fetchUserAttributes();
      logger.debug('✅ [AuthContext] Found authenticated user:', attributes.email);

      // Get session with automatic token refresh
      const session = await fetchAuthSession();
      logger.debug('🔒 [AuthContext] Session retrieved, tokens valid:', !!session.tokens?.idToken);

      // Verify session has valid tokens
      if (!session.tokens?.idToken || !session.tokens?.accessToken) {
        throw new Error('No valid tokens in session');
      }

      const userData = {
        email: attributes.email || '',
        sub: currentUser.userId || attributes.sub || '',
      };
      setUser(userData);
      logger.debug('✅ [AuthContext] User state updated successfully with valid session');

      // Get or create user profile in DynamoDB
      try {
        logger.debug('📦 [AuthContext] Fetching user profile from DynamoDB...');
        const profile = await UserService.getMyProfile();
        setUserProfile(profile);
        logger.debug('✅ [AuthContext] User profile loaded:', profile);
      } catch (profileError: any) {
        if (profileError.response?.status === 404) {
          // User profile doesn't exist (admin-invited user without profile yet).
          // Self sign-up users get their profile created in confirmSignUp before
          // checkAuthState runs, so this branch only fires for invited admins.
          logger.info('🆕 [AuthContext] Creating new user profile (invited user)...');
          try {
            const newProfile = await UserService.updateMyProfile({
              display_name: userData.email.split('@')[0],
            });
            setUserProfile(newProfile);
            logger.info('✅ [AuthContext] User profile created:', newProfile);
          } catch (createError) {
            logger.error('❌ [AuthContext] Failed to create user profile:', createError);
          }
        } else {
          logger.error('❌ [AuthContext] Failed to fetch user profile:', profileError);
        }
      }

    } catch (err: any) {
      // This is expected when user is not authenticated
      logger.debug('🔍 [AuthContext] User is not authenticated:', err.message || err);
      setUser(null);
      setUserProfile(null);
      setError(null); // Don't show error for unauthenticated state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthState();
  }, [checkAuthState]);

  // Hub events for multi-tab authentication sync
  useEffect(() => {
    logger.debug('🔗 [AuthContext] Setting up Hub listeners...');

    // Check if Hub is available (it might not be in test environment)
    if (!Hub || typeof Hub.listen !== 'function') {
      logger.warn('⚠️ [AuthContext] Hub not available, skipping listener setup');
      return;
    }

    const hubListener = (data: any) => {
      const { event } = data.payload;
      logger.debug('🔔 [AuthContext] Hub event received:', event);

      switch (event) {
        case 'signInWithRedirect':
        case 'signedIn':
          logger.info('✅ [AuthContext] Hub: User signed in, refreshing state');
          checkAuthState();
          break;
        case 'signedOut':
          logger.info('🚪 [AuthContext] Hub: User signed out, clearing state');
          setUser(null);
          setUserProfile(null);
          setError(null);
          break;
        case 'tokenRefresh':
          logger.debug('🔄 [AuthContext] Hub: Token refreshed');
          break;
        case 'tokenRefresh_failure':
          logger.error('❌ [AuthContext] Hub: Token refresh failed, clearing state');
          setUser(null);
          setUserProfile(null);
          break;
        default:
          logger.debug('🔔 [AuthContext] Hub: Other event:', event);
      }
    };

    // Listen to authentication events
    const unsubscribe = Hub.listen('auth', hubListener);

    return () => {
      logger.debug('🔌 [AuthContext] Removing Hub listeners');
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [checkAuthState]);

  // #108: クロスタブのログアウト同期。
  // 別タブで Cognito トークンが削除された（= ログアウト or wipe）ら、このタブの認証
  // state もクリアする。storage イベントは変更を起こしたタブ自身には発火しないため
  // ループしない。トークンリフレッシュ（値の更新）は無視し、削除のみ反応させてチャーンを防ぐ。
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      // event.key === null は localStorage.clear()。それ以外は Cognito キーの削除のみ対象。
      const isCognitoRemoval =
        event.newValue === null && (event.key === null || isCognitoStorageKey(event.key));
      if (!isCognitoRemoval) {
        return;
      }
      logger.info('🔁 [AuthContext] Cross-tab logout detected; clearing auth state');
      setUser(null);
      setUserProfile(null);
      setError(null);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const logout = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      logger.info('🚪 [AuthContext] Signing out...');
      await amplifySignOut();
      logger.info('✅ [AuthContext] Sign out successful');
    } catch (err) {
      // amplifySignOut() が失敗してもローカルのトークンを確実に削除する。
      // caller (UserMenuIcon, UserAccountInfo) は catch でエラーを握りつぶすが、
      // UserMenuIcon は handleClose() を try 内でのみ呼ぶため、throw するとドロップダウンが閉じなくなる。
      // フォールバックでトークンを wipe してログアウトを完遂させる (#101)。
      logger.error('❌ [AuthContext] amplifySignOut failed; purging localStorage tokens directly', err);
      wipeCognitoLocalStorageTokens();
    } finally {
      setUser(null);
      setUserProfile(null);
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('🔑 [AuthContext] Login attempt for:', email);

      const result = await amplifySignIn({ username: email, password });
      logger.debug('✅ [AuthContext] SignIn result:', result);

      // Check if user needs to complete additional steps
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        logger.info('🔐 [AuthContext] User needs to change password');
        const error = new Error('Password change required') as any;
        error.code = 'NEW_PASSWORD_REQUIRED';
        error.nextStep = result.nextStep;
        throw error;
      }

      logger.info('✅ [AuthContext] SignIn successful, checking auth state...');
      await checkAuthState();
      logger.debug('✅ [AuthContext] Auth state check completed');
    } catch (err: any) {
      logger.error('❌ [AuthContext] Login error:', err);
      if (err.code === 'NEW_PASSWORD_REQUIRED') {
        // Re-throw for handling in the component
        throw err;
      } else if (err.name === 'UserNotFoundException') {
        setError('ユーザーが見つかりません');
      } else if (err.name === 'NotAuthorizedException') {
        setError('ユーザー名またはパスワードが正しくありません');
      } else if (err.name === 'UserNotConfirmedException') {
        // Re-throw so Login page can redirect to /signup confirm step
        throw err;
      } else {
        setError('ログインに失敗しました。もう一度お試しください。');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('📝 [AuthContext] SignUp attempt for:', email);

      const result = await amplifySignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
      logger.debug('✅ [AuthContext] SignUp result:', result);

      // Hold the password and displayName in memory until confirmSignUp completes
      setPendingSignUp({ email, password, displayName });

      // amplify v6: codeDeliveryDetails only exists on the CONFIRM_SIGN_UP step
      const nextStep = result.nextStep as { codeDeliveryDetails?: { destination?: string } };
      return {
        destination: nextStep?.codeDeliveryDetails?.destination,
      };
    } catch (err: any) {
      logger.error('❌ [AuthContext] SignUp error:', err);
      // Re-throw all sign-up errors so Signup page can render the right UX
      // (UsernameExistsException → duplicate-mail flow, etc.)
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const confirmSignUp = async (email: string, code: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('🔐 [AuthContext] ConfirmSignUp for:', email);

      await amplifyConfirmSignUp({ username: email, confirmationCode: code });
      logger.debug('✅ [AuthContext] ConfirmSignUp succeeded');

      const pending = pendingSignUp;
      if (!pending || pending.email !== email) {
        // Reload mid-flow → password/displayName state lost.
        // Throw so Signup page can redirect to /login with email pre-filled.
        const stateError = new Error('Sign-up state lost; please log in') as any;
        stateError.name = 'PendingSignUpStateLost';
        throw stateError;
      }

      // Sign in directly (NOT via this.login) so checkAuthState only runs once,
      // AFTER we've already created the UserProfile with the right display_name.
      try {
        await amplifySignIn({ username: email, password: pending.password });
      } catch (signInError: any) {
        logger.error('❌ [AuthContext] Auto-login after confirmSignUp failed:', signInError);
        // Re-throw with a recognisable name so Signup page can fall back to /login
        signInError.code = 'AUTO_LOGIN_FAILED';
        throw signInError;
      }

      // Create UserProfile with the exact display_name from the sign-up form.
      // Doing this BEFORE checkAuthState skips the auto-create-from-email path.
      // Retry up to 3 times so a transient failure does not lose the user's
      // chosen display_name (FR-A1). If all retries fail, fall through to
      // checkAuthState — its invited-user branch will create a profile with
      // email-left-side as a best-effort fallback, and Phase 3 mypage lets
      // the user fix it later.
      const PROFILE_MAX_ATTEMPTS = 3;
      let profileSaved = false;
      for (let attempt = 1; attempt <= PROFILE_MAX_ATTEMPTS; attempt += 1) {
        try {
          await UserService.updateMyProfile({ display_name: pending.displayName });
          profileSaved = true;
          break;
        } catch (profileError) {
          logger.warn(
            `⚠️ [AuthContext] Profile creation attempt ${attempt}/${PROFILE_MAX_ATTEMPTS} failed:`,
            profileError,
          );
          if (attempt < PROFILE_MAX_ATTEMPTS) {
            const delay = 200 * 2 ** (attempt - 1); // 200ms, 400ms
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }
      if (!profileSaved) {
        logger.error(
          '❌ [AuthContext] Could not save display_name from sign-up. Falling back to email-left-side; user can edit on /mypage later.',
        );
      }

      await checkAuthState();
      setPendingSignUp(null);
      logger.info('✅ [AuthContext] ConfirmSignUp flow completed');
    } catch (err: any) {
      logger.error('❌ [AuthContext] ConfirmSignUp error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const resendSignUpCode = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('📨 [AuthContext] Resending sign-up code for:', email);
      await amplifyResendSignUpCode({ username: email });
      logger.info('✅ [AuthContext] Resend code succeeded');
    } catch (err: any) {
      logger.error('❌ [AuthContext] Resend code error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('📧 [AuthContext] Password reset request for:', email);

      await amplifyResetPassword({ username: email });
      logger.info('✅ [AuthContext] Password reset email sent successfully');
    } catch (err: any) {
      logger.error('❌ [AuthContext] Forgot password error:', err);
      if (err.name === 'UserNotFoundException') {
        // Don't reveal if user exists for security - show generic message
        logger.debug('⚠️ [AuthContext] User not found, but showing generic message for security');
      } else if (err.name === 'InvalidParameterException') {
        // #184: Cognito は未確認 (UNCONFIRMED) ユーザーにも InvalidParameterException を返す。
        // 画面側 (ForgotPassword) が isUnverifiedEmailResetError で分岐するので、ここは汎用文言にとどめる。
        setError('このメールアドレスではパスワードをリセットできません。登録が完了していない可能性があります');
        throw err;
      } else if (err.name === 'LimitExceededException') {
        setError('リクエストが多すぎます。しばらく時間をおいてからお試しください');
        throw err;
      } else {
        setError('パスワードリセットの要求に失敗しました');
        throw err;
      }
      logger.debug('🔒 [AuthContext] Showing generic success message for security');
    } finally {
      setLoading(false);
    }
  };

  const confirmForgotPassword = async (email: string, code: string, newPassword: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('🔐 [AuthContext] Confirming password reset for:', email);

      await amplifyConfirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
      logger.info('✅ [AuthContext] Password reset confirmed successfully');
    } catch (err: any) {
      logger.error('❌ [AuthContext] Confirm forgot password error:', err);
      if (err.name === 'CodeMismatchException') {
        setError('確認コードが正しくありません');
      } else if (err.name === 'ExpiredCodeException') {
        setError('確認コードの有効期限が切れています。新しいコードをリクエストしてください');
      } else if (err.name === 'InvalidPasswordException') {
        setError('パスワードは8文字以上で、数字を含む必要があります');
      } else if (err.name === 'LimitExceededException') {
        setError('試行回数が多すぎます。しばらく時間をおいてからお試しください');
      } else {
        setError('パスワードリセットの確認に失敗しました');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const completeNewPassword = async (newPassword: string) => {
    try {
      setError(null);
      setLoading(true);
      logger.info('🔐 [AuthContext] Completing new password requirement');

      const result = await amplifyConfirmSignIn({ challengeResponse: newPassword });
      logger.info('✅ [AuthContext] New password set successfully:', result);

      await checkAuthState();
      logger.debug('✅ [AuthContext] Auth state updated after password change');
    } catch (err: any) {
      logger.error('❌ [AuthContext] Complete new password error:', err);
      if (err.name === 'InvalidPasswordException') {
        setError('パスワードは8文字以上で、数字を含む必要があります');
      } else {
        setError('パスワードの設定に失敗しました');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        loading,
        error,
        login,
        logout,
        checkAuthState,
        signUp,
        confirmSignUp,
        resendSignUpCode,
        forgotPassword,
        confirmForgotPassword,
        completeNewPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
